import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaStar, FaArrowRight, FaShieldAlt, FaRocket, FaGift, 
  FaChartLine, FaUsers, FaTrophy, FaCheckCircle, FaPlay,
  FaChevronDown, FaFire, FaCoins, FaClock, FaMobileAlt,
  FaCreditCard, FaGamepad, FaDice, FaCrown, FaLock,
  FaThumbsUp, FaAward, FaBell, FaNewspaper, FaQrcode,
  FaHeadset, FaGlobe, FaPercent, FaHistory, FaSpinner,
  FaHeart, FaEye, FaSearch, FaFilter, FaSort, FaTags,
  FaBookmark, FaShare, FaComment, FaDownload, FaUpload,
  FaSync, FaRandom, FaMagic, FaBolt, FaGem, FaMedal,
  FaTimes
} from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Home: React.FC = () => {
  const [activeOffer, setActiveOffer] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);
  const [selectedNews, setSelectedNews] = useState<number | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Block scroll when modal is open
  useEffect(() => {
    if (selectedNews !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedNews]);

  // Animate stats on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targets = [150, 50, 2, 9.8];
            targets.forEach((target, index) => {
              let current = 0;
              const increment = target / 50;
              const timer = setInterval(() => {
                current += increment;
                if (current >= target) {
                  current = target;
                  clearInterval(timer);
                }
                setAnimatedStats((prev) => {
                  const newStats = [...prev];
                  newStats[index] = Math.floor(current);
                  return newStats;
                });
              }, 30);
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    const statsElement = document.getElementById('animated-stats');
    if (statsElement) observer.observe(statsElement);

    return () => observer.disconnect();
  }, []);

  const offers = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      bonus: '100% up to £100',
      rating: 4.8,
      features: ['Fast Withdrawals', 'UK Licensed', 'Live Casino'],
      link: '/offers/allbritishcasino',
      url: 'https://www.allbritishcasino.com/',
      filters: ['fast-payout', 'live-casino', 'mobile']
    },
    {
      id: 'funcasino',
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      bonus: '200% up to £200',
      rating: 4.7,
      features: ['VIP Program', 'Mobile Optimized', '24/7 Support'],
      link: '/offers/funcasino',
      url: 'https://www.funcasino.com/',
      filters: ['vip', 'mobile']
    },
    {
      id: 'playojo',
      name: 'PlayOJO',
      image: '/images/offers/playojo.webp',
      bonus: '50 Free Spins',
      rating: 4.9,
      features: ['No Wagering', 'OJOplus Rewards', 'Fair Play'],
      link: '/offers/playojo',
      url: 'https://www.playojo.com',
      filters: ['no-wagering', 'mobile']
    },
    {
      id: 'casinocasino',
      name: 'CasinoCasino',
      image: '/images/offers/casinocasino.webp',
      bonus: '£150 Welcome Bonus',
      rating: 4.6,
      features: ['Classic Games', 'Fast Payouts', 'Secure Platform'],
      link: '/offers/casinocasino',
      url: 'https://www.casinocasino.com',
      filters: ['fast-payout', 'mobile']
    },
    {
      id: 'mrplay',
      name: 'MrPlay',
      image: '/images/offers/mrplay.webp',
      bonus: '100% + 200 Spins',
      rating: 4.7,
      features: ['Massive Game Library', 'Daily Promotions', 'Mobile First'],
      link: '/offers/mrplay',
      url: 'https://www.mrplay.com/',
      filters: ['mobile', 'live-casino']
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      bonus: '£100 Welcome Bonus',
      rating: 4.6,
      features: ['Party Points Rewards', 'Live Tournaments', 'Premium Games'],
      link: '/offers/partycasino',
      url: 'https://www.partycasino.com/',
      filters: ['live-casino', 'vip', 'mobile']
    }
  ];

  // Filter offers based on selected filter
  const filteredOffers = selectedFilter === 'all' 
    ? offers 
    : offers.filter(offer => offer.filters?.includes(selectedFilter));

  const stats = [
    { label: 'Casinos Reviewed', value: '150+', icon: FaChartLine },
    { label: 'Active Players', value: '50K+', icon: FaUsers },
    { label: 'Total Bonuses', value: '£2M+', icon: FaGift },
    { label: 'Trust Score', value: '9.8/10', icon: FaShieldAlt },
  ];

  const chartData = [
    { month: 'Jan', bonuses: 120, players: 4500 },
    { month: 'Feb', bonuses: 150, players: 5200 },
    { month: 'Mar', bonuses: 180, players: 6100 },
    { month: 'Apr', bonuses: 200, players: 7200 },
    { month: 'May', bonuses: 240, players: 8500 },
    { month: 'Jun', bonuses: 280, players: 9800 },
  ];

  const newsItems = [
    {
      title: 'New UK Casino Regulations 2025',
      date: 'Dec 5, 2025',
      excerpt: 'Latest updates on UK gambling regulations and how they affect players.',
      icon: FaNewspaper,
      fullContent: `The UK Gambling Commission has announced significant regulatory changes that will come into effect in 2025. These updates focus on enhanced player protection, stricter advertising rules, and improved responsible gambling measures.

Key changes include:
• Stricter affordability checks for all players
• Enhanced self-exclusion programs across all operators
• New requirements for bonus terms transparency
• Mandatory cooling-off periods for large deposits

These regulations aim to create a safer gambling environment while maintaining the competitive nature of the UK market. Players can expect more transparent terms and better protection mechanisms when playing at licensed casinos.

Operators will need to adapt their systems and processes to comply with these new requirements, which may result in some changes to bonus offers and promotional activities. However, the overall goal is to ensure a fair and safe gaming experience for all UK players.`
    },
    {
      title: 'Best Bonuses This Month',
      date: 'Dec 3, 2025',
      excerpt: 'Top casino bonuses available right now. Limited time offers you don\'t want to miss.',
      icon: FaGift,
      fullContent: `December brings some of the most generous casino bonuses we've seen this year. Here are the top offers that stand out for their value and fair terms:

All British Casino - 100% Match Bonus up to £100 + 50 Free Spins
This offer combines a substantial match bonus with free spins on popular slots. The wagering requirement is set at 35x, which is competitive in the current market.

Fun Casino - 200% Welcome Bonus up to £200
One of the highest match percentages available, this bonus doubles your first deposit. Perfect for players looking to maximize their starting balance.

PlayOJO - 50 Free Spins with No Wagering Requirements
The standout feature here is the complete absence of wagering requirements. Any winnings from these free spins are yours to withdraw immediately.

These bonuses are time-limited, so we recommend claiming them soon. Always read the full terms and conditions before accepting any bonus offer to ensure you understand all requirements and restrictions.`
    },
    {
      title: 'Mobile Gaming Trends',
      date: 'Nov 28, 2025',
      excerpt: 'How mobile gaming is changing the online casino landscape in the UK.',
      icon: FaMobileAlt,
      fullContent: `Mobile gaming continues to dominate the UK online casino market, with over 70% of players now accessing casinos primarily through their smartphones and tablets. This trend has fundamentally changed how operators design and deliver their gaming experiences.

Key trends shaping mobile casino gaming:

1. Native Mobile Apps
More casinos are developing dedicated iOS and Android apps that offer superior performance compared to browser-based play. These apps provide faster loading times, smoother gameplay, and better integration with device features.

2. Touch-Optimized Interfaces
Game developers are creating interfaces specifically designed for touch interactions, making mobile play more intuitive and enjoyable than ever before.

3. Live Casino on Mobile
What was once limited to desktop is now fully available on mobile. Players can enjoy live dealer games with HD streaming and interactive features on their phones.

4. Mobile-First Bonuses
Many casinos now offer exclusive mobile bonuses, recognizing the importance of this platform. These can include mobile-only free spins or deposit bonuses.

5. Progressive Web Apps (PWAs)
Some operators are implementing PWAs that combine the best of both worlds - app-like experience without requiring an app store download.

The future of mobile casino gaming looks bright, with continued improvements in technology making mobile play increasingly comparable to desktop experiences.`
    }
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* JSON-LD SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "SpinTitanium",
            "url": "https://spintitanium.com",
            "description": "Comprehensive reviews and comparisons of the best UK casino offers. Find trusted casinos, exclusive bonuses, and expert insights.",
            "publisher": {
              "@type": "Organization",
              "name": "SpinTitanium",
              "logo": {
                "@type": "ImageObject",
                "url": "https://spintitanium.com/images/logo.webp"
              }
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://spintitanium.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />

      {/* Hero Section with 6 Anchors */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
        style={{
          backgroundImage: `url('/images/heromainbg.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-titanium-950/90 via-titanium-950/80 to-titanium-950"></div>
        
        <div className="relative z-10 container mx-auto px-4 py-20 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-neon-blue/20 border border-neon-blue/50 rounded-full px-4 py-2 mb-6">
              <FaFire className="text-neon-blue" />
              <span className="text-neon-blue font-semibold">UK's #1 Casino Review Platform</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="text-gradient">Find Your Perfect</span>
              <br />
              <span className="text-white">Casino Match</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
              We've tested hundreds of UK casinos so you don't have to. Get exclusive bonuses, 
              honest reviews, and find the casino that fits your style.
            </p>

            {/* 6 Anchor Links */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12">
              {[
                { id: 'top-offers', label: 'Top Offers', icon: FaGift },
                { id: 'comparison', label: 'Compare', icon: FaChartLine },
                { id: 'reviews', label: 'Reviews', icon: FaStar },
                { id: 'bonuses', label: 'Bonuses', icon: FaCoins },
                { id: 'features', label: 'Features', icon: FaRocket },
                { id: 'stats', label: 'Statistics', icon: FaTrophy },
              ].map((anchor) => (
                <button
                  key={anchor.id}
                  onClick={() => scrollToSection(anchor.id)}
                  className="glass border-2 border-titanium-800 hover:border-neon-blue rounded-xl p-4 transition-all duration-300 transform hover:scale-105 group"
                >
                  <anchor.icon className="text-2xl text-neon-blue mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold block">{anchor.label}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/comparison"
                className="bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-neon-blue/50 flex items-center justify-center gap-2"
              >
                Compare Casinos
                <FaArrowRight />
              </Link>
              <button
                onClick={() => scrollToSection('top-offers')}
                className="glass border-2 border-neon-blue/50 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
              >
                View Top Offers
                <FaChevronDown />
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10  transform -translate-x-1/2 animate-bounce">
          <FaChevronDown className="text-neon-blue text-2xl" />
        </div>
      </section>

      {/* Top Offers Section */}
      <section id="top-offers" className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Exclusive Casino Offers</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Hand-picked offers from trusted UK casinos. Each one verified for fairness, 
              security, and player satisfaction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className={`glass rounded-2xl p-6 border-2 transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                  activeOffer === index ? 'border-neon-blue shadow-lg shadow-neon-blue/20' : 'border-titanium-800'
                }`}
                onMouseEnter={() => setActiveOffer(index)}
              >
                <div className="flex items-center justify-between mb-4">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="h-12 w-auto"
                    loading="lazy"
                  />
                  <div className="flex items-center gap-1 text-yellow-400">
                    <FaStar />
                    <span className="text-white font-semibold">{offer.rating}</span>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">{offer.name}</h3>
                <div className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold py-2 px-4 rounded-lg mb-4 inline-block">
                  {offer.bonus}
                </div>
                
                <ul className="space-y-2 mb-6">
                  {offer.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <FaCheckCircle className="text-neon-blue flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex gap-3">
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    Play Now
                    <FaArrowRight />
                  </a>
                  <Link
                    to={offer.link}
                    className="bg-titanium-800 hover:bg-titanium-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center"
                  >
                    Review
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 text-center border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300"
              >
                <stat.icon className="text-4xl text-neon-blue mb-4 mx-auto" />
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Chart Section */}
      <section id="comparison" className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Market Insights</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Real data from thousands of UK players
            </p>
          </div>

          <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334e68" />
                <XAxis dataKey="month" stroke="#9fb3c8" />
                <YAxis yAxisId="left" stroke="#9fb3c8" label={{ value: 'Bonuses', angle: -90, position: 'insideLeft', fill: '#9fb3c8' }} />
                <YAxis yAxisId="right" orientation="right" stroke="#9fb3c8" label={{ value: 'Players (K)', angle: 90, position: 'insideRight', fill: '#9fb3c8' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#102a43',
                    border: '1px solid #334e68',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="bonuses"
                  stroke="#00f0ff"
                  strokeWidth={3}
                  dot={{ fill: '#00f0ff', r: 6 }}
                  activeDot={{ r: 8 }}
                  name="Bonuses"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="players"
                  stroke="#b026ff"
                  strokeWidth={3}
                  dot={{ fill: '#b026ff', r: 6 }}
                  activeDot={{ r: 8 }}
                  name="Players"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-neon-blue"></div>
                <span className="text-gray-300 text-sm">Bonuses</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-neon-purple"></div>
                <span className="text-gray-300 text-sm">Players (in thousands)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Why Choose SpinTitanium?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FaShieldAlt,
                title: '100% Trusted Reviews',
                description: 'Every casino we review undergoes rigorous testing. We check licenses, security, game fairness, and payout speeds.'
              },
              {
                icon: FaRocket,
                title: 'Exclusive Bonuses',
                description: 'Get access to special offers you won\'t find anywhere else. We negotiate better deals for our community.'
              },
              {
                icon: FaClock,
                title: 'Real-Time Updates',
                description: 'Our team constantly monitors casinos for changes, new offers, and any issues that might affect players.'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="glass rounded-xl p-8 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105"
              >
                <feature.icon className="text-5xl text-neon-blue mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">In-Depth Casino Reviews</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Detailed analysis of every aspect that matters to players
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div
                key={offer.id}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 group"
              >
                <img
                  src={offer.image}
                  alt={offer.name}
                  className="w-full h-32 object-contain mb-4 rounded-lg"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-neon-blue transition-colors">
                  {offer.name}
                </h3>
                <div className="flex items-center gap-2 text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={i < Math.floor(offer.rating) ? 'fill-current' : 'text-gray-600'} />
                  ))}
                  <span className="text-white ml-2">{offer.rating}</span>
                </div>
                <div className="flex gap-2">
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center text-sm"
                  >
                    Play Now
                  </a>
                  <Link
                    to={offer.link}
                    className="bg-titanium-800 hover:bg-titanium-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center text-sm flex items-center justify-center gap-1"
                  >
                    Review
                    <FaArrowRight className="text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section id="bonuses" className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Latest Bonus Offers</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
              <div className="space-y-6">
                {offers.map((offer, index) => (
                  <div
                    key={offer.id}
                    className="flex items-center justify-between p-4 bg-titanium-900 rounded-lg border border-titanium-800 hover:border-neon-blue transition-colors"
                  >
                    <div>
                      <h4 className="text-white font-bold text-lg">{offer.name}</h4>
                      <p className="text-neon-blue font-semibold">{offer.bonus}</p>
                      <p className="text-gray-500 text-sm">Valid until Dec 31</p>
                    </div>
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-2 px-6 rounded-lg transition-all duration-300"
                    >
                      Claim Now
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Game Categories Section */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Popular Game Categories</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Explore thousands of games across all categories
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: FaDice, name: 'Slots', count: '5000+', color: 'from-neon-blue to-neon-purple' },
              { icon: FaGamepad, name: 'Table Games', count: '200+', color: 'from-neon-purple to-neon-pink' },
              { icon: FaCrown, name: 'Live Casino', count: '150+', color: 'from-neon-pink to-neon-blue' },
              { icon: FaTrophy, name: 'Jackpots', count: '50+', color: 'from-yellow-400 to-orange-500' },
            ].map((category, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 text-center group cursor-pointer"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${category.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <category.icon className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{category.name}</h3>
                <p className="text-neon-blue font-semibold">{category.count} Games</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Secure Payment Options</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Fast, secure, and trusted payment methods
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {['Visa', 'Mastercard', 'PayPal', 'Skrill', 'Neteller', 'Bank Transfer'].map((method, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 flex items-center justify-center group"
              >
                <div className="text-center">
                  <FaCreditCard className="text-4xl text-neon-blue mb-2 mx-auto group-hover:scale-110 transition-transform" />
                  <span className="text-white font-semibold text-sm">{method}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Experience Section */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-gradient">Play Anywhere, Anytime</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                All recommended casinos offer seamless mobile experiences. Play on your smartphone or tablet 
                with optimized apps and mobile-responsive websites.
              </p>
              <div className="space-y-4">
                {[
                  'iOS & Android Apps Available',
                  'Instant Play in Browser',
                  'Touch-Optimized Controls',
                  'Full Game Library on Mobile'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <FaCheckCircle className="text-neon-blue flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="glass rounded-2xl p-8 border-2 border-neon-blue/30">
                <FaMobileAlt className="text-9xl text-neon-blue/50 mx-auto" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-gradient-to-br from-neon-blue to-neon-purple rounded-full p-4 animate-pulse">
                    <FaPlay className="text-white text-2xl" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">What Players Say</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Real feedback from our community
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Sarah M.',
                rating: 5,
                text: 'SpinTitanium helped me find the perfect casino. The reviews are spot-on and the bonuses are real!',
                casino: 'All British Casino'
              },
              {
                name: 'James K.',
                rating: 5,
                text: 'Finally, honest reviews without the marketing fluff. Withdrawal times matched exactly what was promised.',
                casino: 'PlayOJO'
              },
              {
                name: 'Emma L.',
                rating: 5,
                text: 'The comparison tool saved me hours of research. Found my new favorite casino in minutes.',
                casino: 'Fun Casino'
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300"
              >
                <div className="flex items-center gap-1 text-yellow-400 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.casino}</p>
                  </div>
                  <FaThumbsUp className="text-neon-blue text-2xl" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">How We Review Casinos</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Our comprehensive testing process ensures you get accurate information
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Sign Up', desc: 'We create real accounts and make deposits', icon: FaQrcode },
              { step: '02', title: 'Test Games', desc: 'Play slots, table games, and live dealers', icon: FaGamepad },
              { step: '03', title: 'Withdraw Funds', desc: 'Test withdrawal speed and reliability', icon: FaCreditCard },
              { step: '04', title: 'Rate & Review', desc: 'Comprehensive scoring across all categories', icon: FaAward },
            ].map((item, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 text-center"
              >
                <div className="text-6xl font-bold text-neon-blue/20 mb-4">{item.step}</div>
                <item.icon className="text-4xl text-neon-blue mb-4 mx-auto" />
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Security & Trust</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Your safety is our top priority
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FaLock,
                title: 'SSL Encryption',
                desc: 'All recommended casinos use 256-bit SSL encryption to protect your data and transactions.'
              },
              {
                icon: FaShieldAlt,
                title: 'UKGC Licensed',
                desc: 'Every casino we recommend holds a valid UK Gambling Commission license for your protection.'
              },
              {
                icon: FaGlobe,
                title: 'Fair Gaming',
                desc: 'We verify that all games use certified Random Number Generators (RNG) for fair play.'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="glass rounded-xl p-8 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300"
              >
                <feature.icon className="text-5xl text-neon-blue mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Latest Casino News</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Stay updated with the latest industry news and updates
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {newsItems.map((news, index) => (
              <div
                key={index}
                onClick={() => setSelectedNews(index)}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 group cursor-pointer"
              >
                {(() => {
                  const IconComponent = news.icon;
                  return <IconComponent className="text-4xl text-neon-blue mb-4 group-hover:scale-110 transition-transform" />;
                })()}
                <div className="text-gray-500 text-sm mb-2">{news.date}</div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-neon-blue transition-colors">
                  {news.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{news.excerpt}</p>
                <div className="flex items-center gap-2 text-neon-blue font-semibold text-sm">
                  Read More
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>

          {/* News Modal */}
          {selectedNews !== null && newsItems[selectedNews] && (
            <div
              className="fixed inset-0 z-[9999] bg-titanium-950/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
              onClick={() => setSelectedNews(null)}
            >
              <div
                className="glass rounded-2xl p-8 max-w-3xl w-full border-2 border-neon-blue/30 shadow-2xl max-h-[90vh] overflow-y-auto animate-scale-in"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-blue to-neon-purple flex items-center justify-center">
                      {(() => {
                        const IconComponent = newsItems[selectedNews].icon;
                        return <IconComponent className="text-3xl text-white" />;
                      })()}
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm mb-1">{newsItems[selectedNews].date}</div>
                      <h2 className="text-2xl font-bold text-white">{newsItems[selectedNews].title}</h2>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNews(null)}
                    className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-titanium-800 rounded-lg"
                    aria-label="Close"
                  >
                    <FaTimes className="text-2xl" />
                  </button>
                </div>

                <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {newsItems[selectedNews].fullContent}
                </div>

                <div className="flex items-center gap-4 mt-8 pt-6 border-t border-titanium-800">
                  <button className="flex items-center gap-2 text-neon-blue hover:text-neon-purple transition-colors">
                    <FaShare />
                    <span>Share</span>
                  </button>
                  <button className="flex items-center gap-2 text-neon-blue hover:text-neon-purple transition-colors">
                    <FaBookmark />
                    <span>Bookmark</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* VIP Programs Section */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Exclusive VIP Programs</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Unlock premium rewards and personalized service
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                tier: 'Bronze',
                benefits: ['5% Cashback', 'Birthday Bonus', 'Priority Support'],
                color: 'from-orange-600 to-orange-800'
              },
              {
                tier: 'Silver',
                benefits: ['10% Cashback', 'Exclusive Bonuses', 'Personal Account Manager'],
                color: 'from-gray-400 to-gray-600'
              },
              {
                tier: 'Gold',
                benefits: ['15% Cashback', 'VIP Events', 'Luxury Gifts', 'Faster Withdrawals'],
                color: 'from-yellow-400 to-yellow-600'
              }
            ].map((program, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105"
              >
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${program.color} flex items-center justify-center`}>
                  <FaCrown className="text-3xl text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white text-center mb-4">{program.tier} Tier</h3>
                <ul className="space-y-2">
                  {program.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <FaCheckCircle className="text-neon-blue flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Withdrawal Speed Comparison */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Fastest Withdrawal Times</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Compare withdrawal speeds across top casinos
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
              <div className="space-y-4">
                {[
                  { casino: 'PlayOJO', time: '24 hours', method: 'All Methods', badge: 'Fastest' },
                  { casino: 'All British Casino', time: '24-48 hours', method: 'E-wallets', badge: 'Very Fast' },
                  { casino: 'Fun Casino', time: '48-72 hours', method: 'Bank Transfer', badge: 'Fast' },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-6 bg-titanium-950 rounded-lg border border-titanium-800 hover:border-neon-blue transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-blue to-neon-purple flex items-center justify-center">
                        <FaHistory className="text-2xl text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-lg">{item.casino}</h4>
                        <p className="text-gray-400 text-sm">{item.method}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-neon-blue font-bold text-xl mb-1">{item.time}</div>
                      <span className="bg-neon-blue/20 text-neon-blue px-3 py-1 rounded-full text-xs font-semibold">
                        {item.badge}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-gradient">24/7 Customer Support</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                All recommended casinos provide round-the-clock support through multiple channels. 
                Get help whenever you need it, day or night.
              </p>
              <div className="space-y-4">
                {[
                  { icon: FaHeadset, text: 'Live Chat - Instant Response' },
                  { icon: FaBell, text: 'Email Support - Detailed Help' },
                  { icon: FaMobileAlt, text: 'Phone Support - Direct Contact' },
                  { icon: FaGlobe, text: 'Multi-language Support' }
                ].map((support, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <support.icon className="text-neon-blue text-2xl flex-shrink-0" />
                    <span className="text-gray-300">{support.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-8 border-2 border-neon-blue/30 text-center">
              <FaHeadset className="text-9xl text-neon-blue/50 mx-auto mb-6" />
              <div className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold py-3 px-6 rounded-lg inline-block">
                Average Response Time: 2 minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Filter Section */}
      <section id="filter-section" className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Filter by Preference</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Find casinos that match your specific needs
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {['all', 'fast-payout', 'no-wagering', 'live-casino', 'mobile', 'vip'].map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                  selectedFilter === filter
                    ? 'bg-gradient-to-r from-neon-blue to-neon-purple text-white shadow-lg'
                    : 'glass border-2 border-titanium-800 text-gray-300 hover:border-neon-blue'
                }`}
              >
                {filter.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOffers.length > 0 ? (
              filteredOffers.map((offer, index) => (
                <div
                  key={offer.id}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className={`glass rounded-2xl p-6 border-2 transition-all duration-500 transform ${
                    hoveredCard === index
                      ? 'border-neon-blue scale-105 shadow-2xl shadow-neon-blue/30'
                      : 'border-titanium-800'
                  }`}
                >
                  <div className="relative overflow-hidden rounded-lg mb-4">
                    <img
                      src={offer.image}
                      alt={offer.name}
                      className={`w-full h-32 object-contain transition-transform duration-500 ${
                        hoveredCard === index ? 'scale-110' : 'scale-100'
                      }`}
                      loading="lazy"
                    />
                    {hoveredCard === index && (
                      <div className="absolute inset-0 bg-gradient-to-t from-neon-blue/20 to-transparent"></div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{offer.name}</h3>
                  <div className="flex items-center gap-2 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        className={`${
                          i < Math.floor(offer.rating)
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-600'
                        } transition-all duration-300 ${
                          hoveredCard === index ? 'scale-125' : ''
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center text-sm"
                    >
                      Play Now
                    </a>
                    <Link
                      to={offer.link}
                      className="bg-titanium-800 hover:bg-titanium-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 text-center text-sm flex items-center justify-center gap-1"
                    >
                      <FaEye />
                      Review
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-400 text-xl">No casinos match this filter. Try another option.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Interactive Stats Counter */}
      <section id="animated-stats" className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Real-Time Statistics</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Live numbers that matter to players
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Casinos Reviewed', value: animatedStats[0], suffix: '+', icon: FaChartLine, color: 'from-neon-blue to-neon-purple' },
              { label: 'Active Players', value: animatedStats[1], suffix: 'K+', icon: FaUsers, color: 'from-neon-purple to-neon-pink' },
              { label: 'Total Bonuses', value: animatedStats[2], suffix: 'M+', prefix: '£', icon: FaGift, color: 'from-neon-pink to-neon-blue' },
              { label: 'Trust Score', value: animatedStats[3], suffix: '/10', icon: FaShieldAlt, color: 'from-yellow-400 to-orange-500' },
            ].map((stat, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-110 text-center group"
              >
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center group-hover:rotate-360 transition-transform duration-500`}>
                  <stat.icon className="text-2xl text-white" />
                </div>
                <div className="text-4xl font-bold text-white mb-2">
                  {stat.prefix}{stat.value}{stat.suffix}
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Providers & Categories */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Top Game Providers</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Premium software providers available at UK casinos
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-16">
            {[
              { 
                name: 'NetEnt', 
                games: '500+',
                icon: FaBolt,
                description: 'Industry leader in slots',
                featured: ['Starburst', 'Gonzo\'s Quest', 'Mega Fortune']
              },
              { 
                name: 'Microgaming', 
                games: '800+',
                icon: FaTrophy,
                description: 'Pioneer of online gaming',
                featured: ['Immortal Romance', 'Mega Moolah', 'Thunderstruck']
              },
              { 
                name: 'Play\'n GO', 
                games: '300+',
                icon: FaGamepad,
                description: 'Mobile-first provider',
                featured: ['Book of Dead', 'Reactoonz', 'Rise of Olympus']
              },
              { 
                name: 'Pragmatic Play', 
                games: '400+',
                icon: FaGem,
                description: 'Award-winning slots',
                featured: ['The Dog House', 'Sweet Bonanza', 'Gates of Olympus']
              },
              { 
                name: 'Evolution Gaming', 
                games: '100+',
                icon: FaCrown,
                description: 'Live casino specialist',
                featured: ['Lightning Roulette', 'Dream Catcher', 'Monopoly Live']
              },
            ].map((provider, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 group"
              >
                <div className="relative mb-4">
                  <div className="w-full h-24 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 rounded-lg flex items-center justify-center group-hover:from-neon-blue/30 group-hover:to-neon-purple/30 transition-all">
                    <provider.icon className="text-4xl text-neon-blue group-hover:scale-125 transition-transform duration-300" />
                  </div>
                </div>
                <h3 className="text-white font-bold mb-1 text-lg">{provider.name}</h3>
                <p className="text-gray-400 text-sm mb-2">{provider.description}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-neon-blue text-sm font-semibold">{provider.games} Games</span>
                  <span className="text-gray-500 text-xs">Available</span>
                </div>
                <div className="pt-3 border-t border-titanium-800">
                  <p className="text-gray-500 text-xs mb-2">Popular titles:</p>
                  <div className="flex flex-wrap gap-1">
                    {provider.featured.map((title, i) => (
                      <span key={i} className="text-gray-400 text-xs bg-titanium-900 px-2 py-1 rounded">
                        {title}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-gradient">Game Categories</span>
            </h3>
            <p className="text-gray-400 text-lg">
              Explore different types of casino games
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Video Slots', count: '2000+', icon: FaDice, color: 'from-neon-blue to-neon-purple' },
              { name: 'Live Casino', count: '150+', icon: FaCrown, color: 'from-neon-purple to-neon-pink' },
              { name: 'Table Games', count: '300+', icon: FaGamepad, color: 'from-neon-pink to-neon-blue' },
              { name: 'Jackpot Slots', count: '100+', icon: FaGem, color: 'from-yellow-400 to-orange-500' },
              { name: 'Roulette', count: '50+', icon: FaCoins, color: 'from-green-400 to-emerald-500' },
              { name: 'Blackjack', count: '80+', icon: FaLock, color: 'from-blue-400 to-cyan-500' },
              { name: 'Baccarat', count: '30+', icon: FaTrophy, color: 'from-purple-400 to-pink-500' },
              { name: 'Poker', count: '40+', icon: FaAward, color: 'from-red-400 to-rose-500' },
            ].map((category, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 cursor-pointer group text-center"
                onClick={() => {
                  const filterSection = document.getElementById('filter-section');
                  if (filterSection) {
                    filterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${category.color} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <category.icon className="text-2xl text-white" />
                </div>
                <h4 className="text-white font-bold mb-1">{category.name}</h4>
                <p className="text-neon-blue text-sm font-semibold">{category.count}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Comparison Tool */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Quick Comparison Tool</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Compare key features at a glance
            </p>
          </div>

          <div className="max-w-7xl mx-auto">
            <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-titanium-800">
                      <th className="text-left py-4 px-4 text-gray-400 font-semibold">Feature</th>
                      {offers.map((offer) => (
                        <th key={offer.id} className="text-center py-4 px-4 min-w-[150px]">
                          <div className="flex flex-col items-center gap-2">
                            <img src={offer.image} alt={offer.name} className="h-10 w-auto" loading="lazy" />
                            <span className="text-white font-bold text-sm">{offer.name}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { 
                        feature: 'Welcome Bonus', 
                        values: [
                          '100% up to £100', 
                          '200% up to £200', 
                          '50 Free Spins',
                          '£150 Welcome Bonus',
                          '100% + 200 Spins',
                          '£100 Welcome Bonus'
                        ] 
                      },
                      { 
                        feature: 'Min Deposit', 
                        values: ['£10', '£20', '£10', '£10', '£10', '£10'] 
                      },
                      { 
                        feature: 'Withdrawal Time', 
                        values: ['24-48h', '48-72h', '24h', '24-48h', '24-48h', '24-48h'] 
                      },
                      { 
                        feature: 'Wagering', 
                        values: ['35x', '40x', '0x', '30x', '35x', '30x'] 
                      },
                      { 
                        feature: 'Mobile App', 
                        values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'] 
                      },
                      { 
                        feature: 'Live Chat', 
                        values: ['24/7', '24/7', '24/7', '24/7', '24/7', '24/7'] 
                      },
                    ].map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={`border-b border-titanium-800 ${rowIndex % 2 === 0 ? 'bg-titanium-950/50' : ''} hover:bg-titanium-800/50 transition-colors`}
                      >
                        <td className="py-4 px-4 text-gray-300 font-medium">{row.feature}</td>
                        {row.values.map((value, colIndex) => (
                          <td key={colIndex} className="py-4 px-4 text-center">
                            <span className="text-white">{value}</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/comparison"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <FaSearch />
                  View Full Comparison
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Tags Cloud */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Popular Tags</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Explore casinos by popular categories
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {[
              { tag: 'No Wagering', filter: 'no-wagering', count: 1 },
              { tag: 'Fast Payout', filter: 'fast-payout', count: 2 },
              { tag: 'Live Casino', filter: 'live-casino', count: 3 },
              { tag: 'Mobile First', filter: 'mobile', count: 6 },
              { tag: 'VIP Program', filter: 'vip', count: 2 },
              { tag: 'Crypto Accepted', filter: 'crypto', count: 0 },
              { tag: 'UK Licensed', filter: 'all', count: 6 },
              { tag: 'High RTP', filter: 'all', count: 6 },
              { tag: 'Progressive Jackpots', filter: 'all', count: 5 },
              { tag: 'Free Spins', filter: 'all', count: 4 },
              { tag: 'Cashback', filter: 'all', count: 3 },
              { tag: 'Tournaments', filter: 'all', count: 2 }
            ].map((item, index) => {
              const isActive = selectedFilter === item.filter;
              return (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedFilter(item.filter);
                    // Scroll to filter section
                    const filterSection = document.getElementById('filter-section');
                    if (filterSection) {
                      filterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className={`group glass rounded-full px-6 py-3 border-2 transition-all duration-300 transform hover:scale-110 flex items-center gap-2 ${
                    isActive
                      ? 'border-neon-blue bg-neon-blue/20 shadow-lg shadow-neon-blue/30'
                      : 'border-titanium-800 hover:border-neon-blue'
                  }`}
                >
                  <FaTags className={`group-hover:rotate-12 transition-transform ${
                    isActive ? 'text-neon-blue' : 'text-neon-blue'
                  }`} />
                  <span className={`font-semibold ${isActive ? 'text-neon-blue' : 'text-white'}`}>
                    {item.tag}
                  </span>
                  <span className={`text-sm transition-colors ${
                    isActive 
                      ? 'text-neon-blue font-bold' 
                      : 'text-gray-500 group-hover:text-neon-blue'
                  }`}>
                    ({item.count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive Rating Breakdown */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Rating Breakdown</span>
            </h2>
            <p className="text-gray-400 text-lg">
              How we score each casino
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { category: 'Game Variety', score: 95, color: 'from-neon-blue to-neon-purple' },
              { category: 'Bonuses & Promotions', score: 88, color: 'from-neon-purple to-neon-pink' },
              { category: 'Customer Support', score: 92, color: 'from-neon-pink to-neon-blue' },
              { category: 'Security & Licensing', score: 98, color: 'from-green-400 to-green-600' },
              { category: 'Withdrawal Speed', score: 90, color: 'from-yellow-400 to-orange-500' },
              { category: 'Mobile Experience', score: 85, color: 'from-blue-400 to-blue-600' },
            ].map((item, index) => (
              <div key={index} className="glass rounded-xl p-6 border-2 border-titanium-800">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-semibold text-lg">{item.category}</span>
                  <span className="text-neon-blue font-bold text-xl">{item.score}%</span>
                </div>
                <div className="w-full bg-titanium-950 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000 ease-out`}
                    style={{ width: `${item.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Social Proof */}
      <section className="py-20 bg-titanium-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">What Players Are Saying</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Real reviews from verified players
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Michael R.',
                location: 'London',
                rating: 5,
                text: 'Found the perfect casino thanks to SpinTitanium. The detailed reviews saved me from making a bad choice.',
                verified: true,
                date: '2 days ago'
              },
              {
                name: 'Lisa T.',
                location: 'Manchester',
                rating: 5,
                text: 'The comparison tool is brilliant! Made it so easy to see which casino offers the best bonuses.',
                verified: true,
                date: '5 days ago'
              },
              {
                name: 'David K.',
                location: 'Birmingham',
                rating: 5,
                text: 'Honest reviews that actually match reality. Withdrawal times were exactly as stated. Highly recommend!',
                verified: true,
                date: '1 week ago'
              }
            ].map((review, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 transform hover:scale-105 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-neon-blue to-neon-purple flex items-center justify-center">
                      <span className="text-white font-bold">{review.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold">{review.name}</span>
                        {review.verified && (
                          <FaCheckCircle className="text-green-400" title="Verified" />
                        )}
                      </div>
                      <span className="text-gray-500 text-sm">{review.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-yellow-400">
                    {[...Array(review.rating)].map((_, i) => (
                      <FaStar key={i} className="fill-current text-xs" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-300 mb-4 italic">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-xs">{review.date}</span>
                  <div className="flex gap-2">
                    <button className="text-gray-500 hover:text-neon-blue transition-colors">
                      <FaThumbsUp />
                    </button>
                    <button className="text-gray-500 hover:text-neon-blue transition-colors">
                      <FaShare />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 bg-titanium-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Trusted & Verified</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Our certifications and partnerships
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { name: 'UKGC Licensed', icon: FaShieldAlt },
              { name: 'SSL Secured', icon: FaLock },
              { name: 'Fair Play Certified', icon: FaAward },
              { name: 'Responsible Gaming', icon: FaThumbsUp }
            ].map((badge, index) => (
              <div
                key={index}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all duration-300 text-center"
              >
                <badge.icon className="text-5xl text-neon-blue mb-4 mx-auto" />
                <p className="text-white font-semibold">{badge.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-titanium-900 via-titanium-950 to-titanium-900">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-gradient">Ready to Find Your Casino?</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Join thousands of players who trust SpinTitanium for honest reviews and exclusive offers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/comparison"
                className="bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-neon-blue/50 flex items-center justify-center gap-2"
              >
                Compare All Casinos
                <FaArrowRight />
              </Link>
              <Link
                to="/faq"
                className="glass border-2 border-neon-blue/50 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
              >
                View FAQ
                <FaPlay />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;


